/* Sourced Society | ss-cart-drawer
   Opens from the header bag icon, updates through the Ajax Cart API and
   re-renders via the Section Rendering API. Exposes window.SsCart. */
(() => {
  if (customElements.get('ss-cart-drawer')) return;

  const root = (window.Shopify && Shopify.routes && Shopify.routes.root) || '/';
  const post = (path, body) => fetch(root + path, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
    body: JSON.stringify(body)
  }).then(async (r) => {
    const data = await r.json().catch(() => ({}));
    if (!r.ok) throw new Error(data.description || data.message || 'Something went wrong.');
    return data;
  });

  class SsCartDrawer extends HTMLElement {
    connectedCallback() {
      this.panel = this.querySelector('.ss-cart__panel');

      /* Take over the header bag icon before the theme's own drawer sees the click */
      document.addEventListener('click', (e) => {
        const icon = e.target.closest('[data-js-cart-icon]');
        if (!icon) return;
        e.preventDefault();
        e.stopPropagation();
        e.stopImmediatePropagation();
        this.open();
      }, true);

      this.addEventListener('click', (e) => this.onClick(e));
      this.addEventListener('submit', (e) => {
        const f = e.target.closest('[data-code-form]');
        if (!f) return;
        e.preventDefault();
        this.applyCode(f.querySelector('input').value.trim());
      });
      document.addEventListener('keydown', (e) => { if (e.key === 'Escape' && this.classList.contains('is-open')) this.close(); });

      window.SsCart = {
        open: () => this.open(),
        close: () => this.close(),
        refresh: () => this.refresh(),
        add: (id, qty = 1) => this.add(id, qty)
      };
      this.loadRecs();
    }

    open() {
      this.classList.add('is-open');
      this.setAttribute('aria-hidden', 'false');
      document.documentElement.classList.add('ss-lock');
      this.lastFocus = document.activeElement;
      setTimeout(() => this.panel && this.panel.focus({ preventScroll: true }), 50);
    }

    close() {
      this.classList.remove('is-open');
      this.setAttribute('aria-hidden', 'true');
      document.documentElement.classList.remove('ss-lock');
      if (this.lastFocus && this.lastFocus.focus) this.lastFocus.focus({ preventScroll: true });
    }

    onClick(e) {
      const t = e.target;
      if (t.closest('[data-cart-close]')) { this.close(); return; }

      const qty = t.closest('[data-qty]');
      if (qty) {
        const line = Number(qty.closest('[data-line]').dataset.line);
        this.change(line, Math.max(0, Number(qty.dataset.qty)));
        return;
      }

      if (t.closest('[data-code-remove]')) { this.applyCode(''); return; }

      const recAdd = t.closest('[data-rec-add]');
      if (recAdd) { this.add(Number(recAdd.dataset.recAdd), 1); }
    }

    async run(task) {
      this.classList.add('is-busy');
      let error = '';
      try {
        error = (await task()) || '';
      } catch (err) {
        error = err.message;
      }
      await this.refresh();
      this.classList.remove('is-busy');
      const box = this.querySelector('[data-cart-error]');
      if (box && error) { box.textContent = error; box.hidden = false; }
    }

    /* Shopify quietly caps the quantity at the stock level, so compare and explain */
    change(line, quantity) {
      return this.run(async () => {
        const cart = await post('cart/change.js', { line, quantity });
        const item = cart.items && cart.items[line - 1];
        if (quantity > 0 && item && item.quantity < quantity) {
          return item.quantity === 1 ? 'Only 1 available in this size.' : 'Only ' + item.quantity + ' available in this size.';
        }
        return '';
      });
    }

    add(id, quantity = 1) {
      return post('cart/add.js', { items: [{ id, quantity }] }).then(() => this.refresh()).then(() => this.open());
    }

    async applyCode(code) {
      this.classList.add('is-busy');
      let error = '';
      try {
        const cart = await post('cart/update.js', { discount: code });
        if (code) {
          const hit = (cart.discount_codes || []).find((d) => d.code.toLowerCase() === code.toLowerCase());
          if (!hit || !hit.applicable) error = 'That code cannot be applied to this bag.';
        }
      } catch (err) {
        error = err.message;
      }
      await this.refresh();
      this.classList.remove('is-busy');
      if (error) {
        const det = this.querySelector('.ss-cart__code');
        if (det) det.open = true;
        const msg = this.querySelector('[data-code-msg]');
        if (msg) { msg.textContent = error; msg.hidden = false; }
      }
    }

    async refresh() {
      try {
        const html = await (await fetch(root + '?section_id=ss-cart-drawer')).text();
        const doc = new DOMParser().parseFromString(html, 'text/html');
        const fresh = doc.querySelector('[data-cart-body]');
        const body = this.querySelector('[data-cart-body]');
        if (fresh && body) {
          const codeOpen = (body.querySelector('.ss-cart__code') || {}).open;
          body.innerHTML = fresh.innerHTML;
          const code = body.querySelector('.ss-cart__code');
          if (code && codeOpen) code.open = true;
        }
        const count = (doc.querySelector('[data-cart-count]') || {}).textContent;
        if (count !== undefined) {
          document.querySelectorAll('[data-js-cart-count]').forEach((el) => { el.textContent = count; });
        }
        this.loadRecs();
      } catch (e) { /* keep current drawer */ }
    }

    async loadRecs() {
      const box = this.querySelector('[data-recs]');
      if (!box || !box.dataset.productId) return;
      const list = box.querySelector('[data-recs-list]');
      const inCart = new Set([...this.querySelectorAll('[data-pid]')].map((li) => li.dataset.pid));
      try {
        const url = root + 'recommendations/products?product_id=' + box.dataset.productId + '&limit=6&section_id=ss-cart-recs';
        const html = await (await fetch(url)).text();
        const doc = new DOMParser().parseFromString(html, 'text/html');
        const items = [...doc.querySelectorAll('[data-recs-items] > li')].filter((li) => !inCart.has(li.dataset.recId)).slice(0, 3);
        if (!items.length) return;
        list.replaceChildren(...items);
        box.hidden = false;
      } catch (e) { /* no recommendations */ }
    }
  }

  customElements.define('ss-cart-drawer', SsCartDrawer);
})();
